"use client";

import { RailFlyOverModal } from "../components/RailFlyOver/RailFlyOverModal";
import { useTranslationsContext } from "./TranslationsContext";
import { FlyOutData } from "./data/getFlyOutData";
import { Locale } from "./locales.types";
import { createContext, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";

const RailFlyOverContext = createContext<{
  localeString: string;
  show: () => void;
  hide: () => void;
  isLoading: boolean;
  matchingLocale?: Locale;
}>({
  localeString: "",
  show: () => {},
  hide: () => {},
  isLoading: false,
  matchingLocale: undefined,
});

export const RailFlyOverContextProvider = ({
  matchingLocale,
  children,
  getFlyOutData,
}: {
  matchingLocale?: Locale;
  children: React.ReactNode;
  getFlyOutData: (matchingLocale: Locale) => Promise<FlyOutData>;
}) => {
  const translations = useTranslationsContext();
  // TODO: potentially refactor to "visible" | "loading" | "mounted" or similar
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [flyOutData, setFlyOutData] = useState<
    (FlyOutData & { title: string }) | null
  >(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      try {
        if (!matchingLocale) {
          return;
        }

        const data = await getFlyOutData(matchingLocale);
        setFlyOutData({
          ...data,
        });
      } catch (error) {
        console.error("Error fetching flyout data:", error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [matchingLocale, translations, getFlyOutData]);

  useEffect(() => {
    if (isVisible) {
      document.body.setAttribute("data-railflyover-open", "true");
    } else {
      document.body.removeAttribute("data-railflyover-open");
    }
  }, [isVisible]);

  return (
    <RailFlyOverContext.Provider
      value={{
        localeString: matchingLocale?.localeString ?? "",
        show: () => setIsVisible(true),
        hide: () => setIsVisible(false),
        isLoading,
        matchingLocale,
      }}
    >
      {children}
      {isMounted &&
        !isLoading &&
        !!flyOutData &&
        createPortal(
          <RailFlyOverModal
            title={flyOutData.title}
            featuredProducts={flyOutData.featuredProducts}
            categories={flyOutData.categories}
            onClose={() => setIsVisible(false)}
            isOpen={isVisible}
          />,
          document.body,
        )}
    </RailFlyOverContext.Provider>
  );
};

export const useRailFlyOverContext = () => {
  return useContext(RailFlyOverContext);
};
